import { useMemo } from "react";

import { useDispatch, useSelector } from "react-redux";

import type {
    BaseActivatedPanelProps,
    SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { ManualSelectionItem } from "app/pages/sales-intelligence/common-components/table-actions-panel";
import {
    selectActiveLeadsType,
    selectSelectedRowDomains,
    setIsRowSelectionEnabled,
    useClearAndCloseRowsSelection,
} from "app/pages/sales-intelligence/modules/leads-search";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import type { AssignableUser } from "app/pages/sales-intelligence/sub-modules/assign-to-users";
import {
    AssignToOtherUsersPanelView,
    trackingService,
    useAssignableUsersQuery,
    useAssignDomainsToUserMutation,
} from "app/pages/sales-intelligence/sub-modules/assign-to-users";
import assignToUserSelectionDropdownFactory from "app/pages/sales-intelligence/sub-modules/company-search/models/table-actions-panel/assign-to-user/factory";

import { useLeadsResultsActionsState } from "../../../hooks/useLeadsResultsActionsState";
import { useAssignTopNLeadsToUserMutation } from "../../../query/mutations/useAssignTopNLeadsToUserMutation";

type Props = BaseActivatedPanelProps;

const AssignToUserPanelContainer = (props: Props) => {
    const dispatch = useDispatch();

    const listParams = useListParams();

    const selectedRowDomains = useSelector(selectSelectedRowDomains);
    const activeLeadsType = useSelector(selectActiveLeadsType);

    const { data: users = [] } = useAssignableUsersQuery();
    const assignDomainsToUserMutation = useAssignDomainsToUserMutation();
    const assignTopNLeadsToUserMutation = useAssignTopNLeadsToUserMutation(activeLeadsType);

    const { areActionsDisabled, numberOfResults } = useLeadsResultsActionsState(activeLeadsType);

    const leadsSelectionDropdown = useMemo(
        () => assignToUserSelectionDropdownFactory(activeLeadsType),
        [activeLeadsType],
    );

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection();

    const handleDeactivate = (): void => {
        clearAndCloseRowsSelection();
        props.onDeactivate();
    };

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>): void => {
        if (item.id === ManualSelectionItem.ID) {
            dispatch(setIsRowSelectionEnabled(true));

            return;
        }

        clearAndCloseRowsSelection();
    };

    const handleAssignSubmit = async (user: AssignableUser, selectionItem: Readonly<SelectionDropdownItem>) => {
        let websites: readonly string[] | undefined;

        if (selectionItem.id === ManualSelectionItem.ID) {
            websites = await assignDomainsToUserMutation.mutateAsync({
                assigneeId: user.id,
                domains: selectedRowDomains,
            });
        } else {
            websites = await assignTopNLeadsToUserMutation.mutateAsync({
                assigneeId: user.id,
                domainsCount: selectionItem.value,
                listParams,
            });
        }

        if (websites) {
            trackingService.trackUserSelected(websites.length, user.name);
        }

        handleDeactivate();
    };

    return (
        <AssignToOtherUsersPanelView
            {...props}
            isDisabled={areActionsDisabled}
            selectionDropdown={leadsSelectionDropdown}
            isLoading={assignDomainsToUserMutation.isPending || assignTopNLeadsToUserMutation.isPending}
            numberOfResults={numberOfResults}
            numberOfSelectedItems={selectedRowDomains.length}
            users={users}
            onAssignSubmit={handleAssignSubmit}
            onSelectionChange={handleSelectionChange}
        />
    );
};

export default AssignToUserPanelContainer;
